import React, { FC, ReactNode, useContext, useEffect, useCallback, useMemo, useRef } from 'react';
import { PersonalInformationsState, PersonalInformationResource, PersonalInfoResourceInfo } from 'we-package-react-personal-informations';
import { AccountResource, AccountResourceInfo, LastLogOnSetup, MfaOnboardingResource, MfaOnboardingResourceInfo } from 'we-package-react-account';
import { ApplicationAvailability, UserAccountContext, ClaimsProvider, AuthenticationContext, useFeatureState, useResourceState } from 'omni-package-react-core';
import getTokenManager from '~/utils/getTokenManager';
import ApplicationVersioningResource from '~/components/ApplicationVersioningResource';
import { AnalyticsGuidResource } from 'omni-package-react-analytics';
import { ClientWelcomeModal } from 'we-package-react-notifications';
import Constants from '~/app/Constants';
import { getAvailabilityConfiguration } from '~/utils/getAvailabilityConfiguration';
import { useLocation } from 'react-router-dom';
import { useScopedSelector } from 'ia-react-core';
import useIsFrom from '~/hooks/useIsFrom';
import { Company } from '~/interfaces/CompanyConfig';
import OnboardingResource, { OnboardingResourceInfo } from '~/components/OnboardingResource';
import TermsOfUseModal from '../../components/TermsOfUseModal';
import AppLoadingIcon from '../../components/AppLoadingIcon';
import AccountSecurityValidator from './AccountSecurityValidator/AccountSecurityValidator';

export interface ContextWrapperProps {
  children: ReactNode;
}

const DIAG_LOG_PREFIX = '[DIAG][ContextWrapper]';

const ContextWrapper: FC<ContextWrapperProps> = ({ children }) => {
  const userAccountCtx = useContext(UserAccountContext);
  const isProspect: boolean = useFeatureState(PersonalInformationsState, (state) => state?.info?.identification?.isProspect);
  const isClient: boolean = useScopedSelector('/App.features.personalInformation.info.identification.behaviours.isClient');

  const authCtx = useContext(AuthenticationContext);
  const tokenManager = useMemo(() => getTokenManager(), []);
  useEffect(() => {
    userAccountCtx.saveIsProspect(isProspect);
    userAccountCtx.saveIsClient(isClient);
  }, [isProspect]);// eslint-disable-line react-hooks/exhaustive-deps

  const location = useLocation();

  const includeAvailabilityCheck = useMemo(() => (
    Constants.AvailabilityCheckRoutes.includes(location.pathname)
  ), [location.pathname]);

  const isPublicRouteWithAuthenticatedLayout = useMemo(() => (
    Constants.PublicRoutesWithAuthenticatedLayout.includes(location.pathname)
  ), [location.pathname]);

  const shouldLoadOnboardingResource = useMemo(() => (
    !isPublicRouteWithAuthenticatedLayout
  ), [isPublicRouteWithAuthenticatedLayout]);

  const accountState = useResourceState(AccountResourceInfo.name);
  const mfaOnboardingState = useResourceState(MfaOnboardingResourceInfo.name);
  const onboardingState = useResourceState(OnboardingResourceInfo.name);
  const personalInfoState = useResourceState(PersonalInfoResourceInfo.name);
  const lastResourceErrorLogKeyRef = useRef('');

  useEffect(() => {
    if (authCtx?.authenticationInfo?.isAuthenticated !== true || !isPublicRouteWithAuthenticatedLayout) {
      return;
    }

    const rawPathname = location.pathname;
    const safePathname = encodeURIComponent(location.pathname);

    const routeDiag = {
      pathname: safePathname,
      rawPathname,
      shouldLoadOnboardingResource,
      authProcessed: authCtx?.authenticationInfo?.isAuthenticationProcessed,
    };

    // eslint-disable-next-line no-console
    console.info(`${DIAG_LOG_PREFIX} public route with authenticated layout ${JSON.stringify(routeDiag)}`);
  }, [
    authCtx?.authenticationInfo?.isAuthenticated,
    authCtx?.authenticationInfo?.isAuthenticationProcessed,
    isPublicRouteWithAuthenticatedLayout,
    location.pathname,
    shouldLoadOnboardingResource,
  ]);

  useEffect(() => {
    if (authCtx?.authenticationInfo?.isAuthenticated !== true || !isPublicRouteWithAuthenticatedLayout) {
      return;
    }

    const rawPathname = location.pathname;
    const safePathname = encodeURIComponent(location.pathname);

    const failingResources = [
      { name: 'account', state: accountState },
      { name: 'personal-info', state: personalInfoState },
      { name: 'mfa-onboarding', state: mfaOnboardingState },
      { name: 'onboarding', state: onboardingState },
    ].filter((item) => Boolean(item.state?.error));

    if (failingResources.length === 0) {
      return;
    }

    const failingResourceDetails = failingResources.map((item) => {
      const resourceError = item.state?.error as { status?: number; response?: { status?: number }; message?: string } | undefined;

      return {
        name: item.name,
        pending: item.state?.pending,
        requestCount: item.state?.requestCount,
        status: resourceError?.status ?? resourceError?.response?.status,
        message: resourceError?.message,
      };
    });

    const key = `${safePathname}:${JSON.stringify(failingResourceDetails)}`;
    if (lastResourceErrorLogKeyRef.current === key) {
      return;
    }
    lastResourceErrorLogKeyRef.current = key;

    const failureDiag = {
      pathname: safePathname,
      rawPathname,
      failingResources: failingResourceDetails,
    };

    // eslint-disable-next-line no-console
    console.warn(`${DIAG_LOG_PREFIX} auth resources failing on public route ${JSON.stringify(failureDiag)}`);
  }, [
    authCtx?.authenticationInfo?.isAuthenticated,
    isPublicRouteWithAuthenticatedLayout,
    location.pathname,
    accountState,
    personalInfoState,
    mfaOnboardingState,
    onboardingState,
  ]);

  const isFromIaah = useIsFrom(Company.IAAH);

  const getContainer = useCallback(() => (includeAvailabilityCheck ? ApplicationAvailability : React.Fragment), [includeAvailabilityCheck]);
  const config = useMemo(() => getAvailabilityConfiguration(), []);

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const p: any = useMemo(() => includeAvailabilityCheck ? { ...config, loading: <AppLoadingIcon />, unavailablePageUrl: Constants.PagesUrl.UnavailablePage } : {}, [config, includeAvailabilityCheck]);
  const Container = getContainer();

  /* istanbul ignore next */
  const tempHasClaim = (claim: string, value?: string): Promise<boolean> => tokenManager.hasClaim(claim, value);

  return (
    <Container {...p}>
      {authCtx?.authenticationInfo?.isAuthenticated === true ?
        <>
          <ApplicationVersioningResource />
          {!isFromIaah &&
          <>
            <AccountResource autoRequestIfEmpty />
            <PersonalInformationResource autoRequestIfEmpty />
            <MfaOnboardingResource autoRequestIfEmpty />
            <AnalyticsGuidResource autoRequest />
            <LastLogOnSetup />
          </>}
          {shouldLoadOnboardingResource && <OnboardingResource />}
          <ClaimsProvider
            termsOfUseClaim="ia_client_space_terms_of_use_accepted"
            dcaClaim="on-behalf-of"
            hasClaim={tempHasClaim}
          >
            <TermsOfUseModal>
              <ClientWelcomeModal />
              <AccountSecurityValidator>
                {children}
              </AccountSecurityValidator>
            </TermsOfUseModal>
          </ClaimsProvider>
        </> : <>{children}</>}
    </Container>
  );
};

export default ContextWrapper;
